import { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  MeetingAttendee,
  MeetingData,
  MeetingFormData,
  DEFAULT_MEETING_FORM_DATA,
  RecentMeeting,
  RepeatOption,
  ResolvedMatch
} from '../interfaces/interface';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import { logger } from 'src/logger';
import { buildMeetingAttendees, computeAuthorizedLogins } from '../utils/attendeeUtils';

/** Metadata passed from MeetingFormContainer when registering a meeting */
interface MeetingRegistrationMetadata {
  subject: string;
  agenda: string;
  duration: number;
  repeat: RepeatOption;
  onBehalfOfLogin: string | null;
}

interface UseMeetingPersistenceProps {
  alias: string;
  /** The alias of the meeting creator (from URL param). Used as DDB partition key for existing meetings. */
  creatorAlias: string | null;
  sharedMeetingId: string | null;
  isSharedCreator: boolean;
  resolvedAttendees: ResolvedMatch[];
  /** Whether the shared meeting is still loading attendees from DDB */
  isLoadingSharedMeeting: boolean;
  addFlashMessage: (type: 'success' | 'error', content: string) => void;
  addRecentMeeting: (meeting: RecentMeeting) => void;
  updateRecentMeetingSubject: (meetingId: string, subject: string) => void;
  setSearchParams: (params: Record<string, string>, options?: { replace?: boolean }) => void;
  markSharedMeetingLoaded?: () => void;
}

interface UseMeetingPersistenceResult {
  meetingFormData: MeetingFormData;
  setMeetingFormData: React.Dispatch<React.SetStateAction<MeetingFormData>>;
  handleInputChange: (field: keyof MeetingFormData, value: string) => void;
  manuallyEditedMeetingFormDataFields: Set<keyof MeetingFormData>;
  isSavingMeeting: boolean;
  hasUnsavedChanges: boolean;
  ensureMeetingCreated: (overrideMetadata?: MeetingRegistrationMetadata) => Promise<string | null>;
  handleSaveMeeting: () => Promise<void>;
  handleCreateSharedLink: (overrideMetadata?: MeetingRegistrationMetadata) => Promise<void>;
  setInitialSnapshot: (snapshot: string) => void;
}

/**
 * Manages meeting persistence: create, save, link sharing, and unsaved changes detection.
 */
export const useMeetingPersistence = ({
  alias,
  creatorAlias,
  sharedMeetingId,
  isSharedCreator,
  resolvedAttendees,
  isLoadingSharedMeeting,
  addFlashMessage,
  addRecentMeeting,
  updateRecentMeetingSubject,
  setSearchParams,
  markSharedMeetingLoaded
}: UseMeetingPersistenceProps): UseMeetingPersistenceResult => {
  // ── Form state (merged from useMeetingForm) ──────────────────────────
  const [meetingFormData, setMeetingFormData] = useState<MeetingFormData>(DEFAULT_MEETING_FORM_DATA);
  const [manuallyEditedMeetingFormDataFields, setManuallyEditedMeetingFormDataFields] = useState<Set<keyof MeetingFormData>>(new Set());

  const handleInputChange = useCallback((field: keyof MeetingFormData, value: string) => {
    setMeetingFormData((prev) => ({ ...prev, [field]: value }));
    setManuallyEditedMeetingFormDataFields((prev) => new Set(prev).add(field));
  }, []);

  // ── Persistence state ────────────────────────────────────────────────
  const [isSavingMeeting, setIsSavingMeeting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const lastSavedAttendeeSnapshot = useRef<string>('');
  const subjectDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialSubjectRef = useRef<string | null>(null);

  const initialLoadCompleteRef = useRef(false);

  const setInitialSnapshot = useCallback((snapshot: string) => {
    lastSavedAttendeeSnapshot.current = snapshot;
    initialLoadCompleteRef.current = false;
  }, []);

  const wasLoadingRef = useRef(false);

  // Track loading state transitions — enable auto-save when loading completes
  // No stabilization timer needed: the auto-save compares against DDB before writing
  useEffect(() => {
    if (isLoadingSharedMeeting) {
      wasLoadingRef.current = true;
      initialLoadCompleteRef.current = false;
      return;
    }

    if (wasLoadingRef.current) {
      wasLoadingRef.current = false;
      initialLoadCompleteRef.current = true;
    }
  }, [isLoadingSharedMeeting]);

  // One-time backfill: persist employeeName/eaLogin for attendees loaded from DDB that lack them
  const backfillDoneRef = useRef(false);
  useEffect(() => {
    if (backfillDoneRef.current || !initialLoadCompleteRef.current || isLoadingSharedMeeting) return;
    if (!sharedMeetingId || !isSharedCreator || resolvedAttendees.length === 0) return;

    const dbAlias = creatorAlias ?? alias;
    EmployeeSettingsService.getMeeting(dbAlias, sharedMeetingId)
      .then((existing) => {
        if (!existing) return;
        const hasMissing = existing.attendees.some((a) => !a.employeeName || !('eaLogin' in a));
        if (!hasMissing) {
          backfillDoneRef.current = true;
          return;
        }
        const attendees = buildMeetingAttendees(resolvedAttendees);
        return EmployeeSettingsService.saveMeeting(dbAlias, { ...existing, attendees }).then(() => {
          backfillDoneRef.current = true;
        });
      })
      .catch(() => {
        /* Will retry on next effect run */
      });
  }, [isLoadingSharedMeeting, sharedMeetingId, isSharedCreator, resolvedAttendees, creatorAlias, alias]);

  // Debounced auto-save for subject line (1 second after user stops typing)
  useEffect(() => {
    if (initialSubjectRef.current === null) {
      initialSubjectRef.current = meetingFormData.subject;
      return;
    }
    if (!sharedMeetingId) return;
    if (meetingFormData.subject === initialSubjectRef.current) return;

    if (subjectDebounceRef.current) {
      clearTimeout(subjectDebounceRef.current);
    }
    const dbAlias = creatorAlias ?? alias;
    subjectDebounceRef.current = setTimeout(() => {
      EmployeeSettingsService.updateMeetingMetadata(dbAlias, sharedMeetingId, { subject: meetingFormData.subject }).catch(() => {});
      updateRecentMeetingSubject(sharedMeetingId, meetingFormData.subject);
    }, 1000);

    return () => {
      if (subjectDebounceRef.current) {
        clearTimeout(subjectDebounceRef.current);
      }
    };
  }, [meetingFormData.subject, sharedMeetingId, alias, creatorAlias]);

  const attendeeAutoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced auto-save for attendees (2 seconds after last change, creator only in shared mode)
  useEffect(() => {
    const currentSnapshot = JSON.stringify(
      resolvedAttendees
        .filter((a) => a.selectedEmployee?.login_name)
        .map((a) => ({ login: a.selectedEmployee!.login_name, priority: a.priority, proxyForLogin: a.proxyForLogin }))
        .sort((a, b) => a.login.localeCompare(b.login))
    );

    const changed = lastSavedAttendeeSnapshot.current !== '' && currentSnapshot !== lastSavedAttendeeSnapshot.current;
    setHasUnsavedChanges(changed);

    // Auto-save when in shared meeting mode and creator has made changes
    // Skip during initial load stabilization period
    if (!changed || !sharedMeetingId || !isSharedCreator || !initialLoadCompleteRef.current || isLoadingSharedMeeting) return;

    if (attendeeAutoSaveRef.current) {
      clearTimeout(attendeeAutoSaveRef.current);
    }

    attendeeAutoSaveRef.current = setTimeout(async () => {
      const dbAlias = creatorAlias ?? alias;
      const attendees = buildMeetingAttendees(resolvedAttendees);

      try {
        // Read existing meeting to preserve attachments and compare before writing
        const existingMeeting = await EmployeeSettingsService.getMeeting(dbAlias, sharedMeetingId);

        // Compare existing DDB attendees with new list — skip save if identical
        const existingAttendeeSnapshot = JSON.stringify(
          (existingMeeting?.attendees ?? [])
            .map((a) => ({ login: a.loginName, priority: a.priority, proxyForLogin: a.proxyForLogin }))
            .sort((a, b) => a.login.localeCompare(b.login))
        );
        const newAttendeeSnapshot = JSON.stringify(
          attendees
            .map((a) => ({ login: a.loginName, priority: a.priority, proxyForLogin: a.proxyForLogin }))
            .sort((a, b) => a.login.localeCompare(b.login))
        );

        if (existingAttendeeSnapshot === newAttendeeSnapshot) {
          // No actual change in DDB data — update local snapshot without writing
          lastSavedAttendeeSnapshot.current = currentSnapshot;
          setHasUnsavedChanges(false);
          return;
        }

        const meetingData: MeetingData = {
          meetingId: sharedMeetingId,
          subject: meetingFormData.subject?.trim() || existingMeeting?.subject || 'Untitled Meeting',
          createdBy: existingMeeting?.createdBy ?? dbAlias,
          createdAt: existingMeeting?.createdAt ?? new Date().toISOString(),
          attendees,
          organizerLogin: existingMeeting?.organizerLogin ?? dbAlias,
          duration: existingMeeting?.duration ?? 30,
          repeat: existingMeeting?.repeat ?? 'none',
          onBehalfOfLogin: meetingFormData.onBehalfOfLogin,
          agenda: meetingFormData.agenda,
          attachments: existingMeeting?.attachments ?? [],
          authorizedLogins: computeAuthorizedLogins(dbAlias, attendees, {
            organizerLogin: existingMeeting?.organizerLogin ?? dbAlias,
            onBehalfOfLogin: meetingFormData.onBehalfOfLogin
          })
        };

        await EmployeeSettingsService.saveMeeting(dbAlias, meetingData);
        lastSavedAttendeeSnapshot.current = currentSnapshot;
        setHasUnsavedChanges(false);

        // Update My Meetings panel with new attendee count
        const meetingUrl = `${window.location.origin}/ea-huddle?meetingId=${sharedMeetingId}&creator=${encodeURIComponent(dbAlias)}`;
        addRecentMeeting({
          meetingId: sharedMeetingId,
          tinyUrl: meetingUrl,
          subject: meetingFormData.subject?.trim() || existingMeeting?.subject || 'Untitled Meeting',
          attendees,
          createdBy: existingMeeting?.createdBy ?? dbAlias,
          createdAt: existingMeeting?.createdAt ?? new Date().toISOString()
        });

        logger.info('Attendees auto-saved', { meetingId: sharedMeetingId, attendeeCount: attendees.length });
      } catch (error) {
        logger.error('Failed to auto-save attendees', { error, meetingId: sharedMeetingId });
      }
    }, 2000);

    return () => {
      if (attendeeAutoSaveRef.current) {
        clearTimeout(attendeeAutoSaveRef.current);
      }
    };
  }, [resolvedAttendees, sharedMeetingId, isSharedCreator, creatorAlias, alias, meetingFormData.subject, meetingFormData.onBehalfOfLogin]);

  /** Build attendee list from resolved attendees */
  const buildAttendeeList = useCallback((): MeetingAttendee[] => {
    return buildMeetingAttendees(resolvedAttendees);
  }, [resolvedAttendees]);

  /** Update the snapshot to mark current state as "saved" */
  const updateSnapshot = useCallback((attendees: MeetingAttendee[]) => {
    lastSavedAttendeeSnapshot.current = JSON.stringify(
      attendees
        .map((a) => ({ login: a.loginName, priority: a.priority, proxyForLogin: a.proxyForLogin }))
        .sort((a, b) => a.login.localeCompare(b.login))
    );
    setHasUnsavedChanges(false);
  }, []);

  const ensureMeetingCreated = useCallback(
    async (overrideMetadata?: MeetingRegistrationMetadata): Promise<string | null> => {
      if (sharedMeetingId) return sharedMeetingId;

      try {
        const meetingId = uuidv4();
        const now = new Date().toISOString();
        const attendees = buildAttendeeList();
        const effectiveSubject = overrideMetadata?.subject ?? meetingFormData.subject;
        const effectiveAgenda = overrideMetadata?.agenda ?? meetingFormData.agenda;
        const effectiveDuration = overrideMetadata?.duration ?? 30;
        const effectiveRepeat = overrideMetadata?.repeat ?? 'none';
        const effectiveOnBehalfOfLogin = overrideMetadata?.onBehalfOfLogin ?? meetingFormData.onBehalfOfLogin;

        const meetingData: MeetingData = {
          meetingId,
          subject: effectiveSubject || 'Untitled Meeting',
          agenda: effectiveAgenda,
          createdBy: alias,
          createdAt: now,
          attendees,
          organizerLogin: alias,
          duration: effectiveDuration,
          repeat: effectiveRepeat,
          onBehalfOfLogin: effectiveOnBehalfOfLogin,
          authorizedLogins: computeAuthorizedLogins(alias, attendees, {
            organizerLogin: alias,
            onBehalfOfLogin: effectiveOnBehalfOfLogin
          })
        };

        await EmployeeSettingsService.saveMeeting(alias, meetingData);
        updateSnapshot(attendees);

        // Enable auto-save for subsequent attendee changes
        initialLoadCompleteRef.current = true;

        // Prevent shared-meeting load useEffect from re-firing
        markSharedMeetingLoaded?.();
        setSearchParams({ meetingId, creator: alias }, { replace: true });
        const newUrl = `${window.location.origin}/ea-huddle?meetingId=${meetingId}&creator=${encodeURIComponent(alias)}`;

        const recentMeeting: RecentMeeting = {
          meetingId,
          tinyUrl: newUrl,
          subject: effectiveSubject || 'Untitled Meeting',
          attendees,
          createdBy: alias,
          createdAt: now
        };
        addRecentMeeting(recentMeeting);

        addFlashMessage('success', 'Meeting created!');
        logger.info('Meeting auto-created', { meetingId, attendeeCount: attendees.length });

        return meetingId;
      } catch (error) {
        logger.error('Failed to create meeting', { error });
        addFlashMessage('error', 'Failed to create meeting. Please try again.');
        return null;
      }
    },
    [
      sharedMeetingId,
      buildAttendeeList,
      meetingFormData.subject,
      meetingFormData.onBehalfOfLogin,
      alias,
      addFlashMessage,
      addRecentMeeting,
      setSearchParams,
      updateSnapshot,
      markSharedMeetingLoaded
    ]
  );

  const handleSaveMeeting = useCallback(async () => {
    if (!sharedMeetingId) return;
    const dbAlias = creatorAlias ?? alias;
    setIsSavingMeeting(true);
    try {
      const attendees = buildAttendeeList();

      const meetingData: MeetingData = {
        meetingId: sharedMeetingId,
        subject: meetingFormData.subject || 'Untitled Meeting',
        createdBy: dbAlias,
        createdAt: new Date().toISOString(),
        attendees,
        organizerLogin: dbAlias,
        duration: 30,
        repeat: 'none',
        onBehalfOfLogin: meetingFormData.onBehalfOfLogin,
        authorizedLogins: computeAuthorizedLogins(dbAlias, attendees, {
          organizerLogin: dbAlias,
          onBehalfOfLogin: meetingFormData.onBehalfOfLogin
        })
      };

      await EmployeeSettingsService.saveMeeting(dbAlias, meetingData);

      const meetingUrl = `${window.location.origin}/ea-huddle?meetingId=${sharedMeetingId}&creator=${encodeURIComponent(dbAlias)}`;
      const recentMeeting: RecentMeeting = {
        meetingId: sharedMeetingId,
        tinyUrl: meetingUrl,
        subject: meetingFormData.subject || 'Untitled Meeting',
        attendees,
        createdBy: alias,
        createdAt: new Date().toISOString()
      };
      addRecentMeeting(recentMeeting);
      updateSnapshot(attendees);

      addFlashMessage('success', 'Meeting saved!');
      logger.info('Meeting updated via shared link', { meetingId: sharedMeetingId, attendeeCount: attendees.length });
    } catch (error) {
      logger.error('Failed to save meeting', { error, meetingId: sharedMeetingId });
      addFlashMessage('error', 'Failed to save meeting. Please try again.');
    } finally {
      setIsSavingMeeting(false);
    }
  }, [sharedMeetingId, buildAttendeeList, meetingFormData.subject, alias, creatorAlias, addFlashMessage, addRecentMeeting, updateSnapshot]);

  const handleCreateSharedLink = useCallback(
    async (overrideMetadata?: MeetingRegistrationMetadata) => {
      if (isSharedCreator) {
        const currentUrl = window.location.href;
        navigator.clipboard.writeText(currentUrl).catch(() => {});
        return;
      }

      const meetingId = await ensureMeetingCreated(overrideMetadata);
      if (meetingId) {
        const meetingUrl = `${window.location.origin}/ea-huddle?meetingId=${meetingId}&creator=${encodeURIComponent(alias)}`;
        navigator.clipboard.writeText(meetingUrl).catch(() => {});
      }
    },
    [isSharedCreator, ensureMeetingCreated, alias, addFlashMessage]
  );

  return {
    meetingFormData,
    setMeetingFormData,
    handleInputChange,
    manuallyEditedMeetingFormDataFields,
    isSavingMeeting,
    hasUnsavedChanges,
    ensureMeetingCreated,
    handleSaveMeeting,
    handleCreateSharedLink,
    setInitialSnapshot
  };
};
